import * as React from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { getSelectedDestinationIdsForApp, setSelectedDestinationIdsForApp } from '@/helpers/localStorageHelper';

const DESTINATION_IDS_PARAM = 'destinationIds';

interface UseDestinationSelectionProps {
    appId: string;
    availableDestinationIds: string[];
}

interface UseDestinationSelectionResult {
    selectedDestinationIds: string[];
    setSelectedDestinationIds: (ids: string[]) => void;
}

export function getOptimalDestinationQueryParams(
    selectedIds: string[],
    availableIds: string[],
): { destinationStepIds?: string[]; excludedDestinationStepIds?: string[] } {
    if (selectedIds.length === 0 || selectedIds.length === availableIds.length) {
        return {};
    }
    const excludedIds = availableIds.filter((id) => !selectedIds.includes(id));
    if (excludedIds.length < selectedIds.length) {
        return { excludedDestinationStepIds: excludedIds };
    }
    return { destinationStepIds: selectedIds };
}

export const useDestinationSelection = ({
    appId,
    availableDestinationIds,
}: UseDestinationSelectionProps): UseDestinationSelectionResult => {
    const history = useHistory();
    const location = useLocation();

    const [selectedDestinationIds, setSelectedDestinationIdsState] = React.useState<string[]>(() => {
        const urlParams = new URLSearchParams(location.search);
        const urlDestinations = urlParams.get(DESTINATION_IDS_PARAM);

        if (urlDestinations) {
            const parsed = urlDestinations.split(',').filter(Boolean);
            const valid = parsed.filter((id) => availableDestinationIds.includes(id));
            if (valid.length > 0) return valid;
        }

        const stored = getSelectedDestinationIdsForApp(appId);
        if (stored.length > 0) {
            const valid = stored.filter((id) => availableDestinationIds.includes(id));
            if (valid.length > 0) return valid;
        }

        return availableDestinationIds;
    });

    React.useEffect(() => {
        const urlParams = new URLSearchParams(location.search);
        if (!urlParams.has(DESTINATION_IDS_PARAM) && selectedDestinationIds.length > 0) {
            urlParams.set(DESTINATION_IDS_PARAM, selectedDestinationIds.join(','));
            const newSearch = urlParams.toString();
            const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
            history.replace(newUrl);
        }
    }, []);

    const setSelectedDestinationIds = React.useCallback(
        (ids: string[]) => {
            setSelectedDestinationIdsState(ids);
            setSelectedDestinationIdsForApp(appId, ids);

            const urlParams = new URLSearchParams(location.search);
            if (ids.length > 0) {
                urlParams.set(DESTINATION_IDS_PARAM, ids.join(','));
            } else {
                urlParams.delete(DESTINATION_IDS_PARAM);
            }

            const newSearch = urlParams.toString();
            const newUrl = newSearch ? `${location.pathname}?${newSearch}` : location.pathname;
            history.replace(newUrl);
        },
        [appId, history, location.pathname, location.search],
    );

    return { selectedDestinationIds, setSelectedDestinationIds };
};
